import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, Tooltip, Chip, Typography, Paper,
    FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, CircularProgress, Collapse,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RouteIcon from '@mui/icons-material/Route';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ChatIcon from '@mui/icons-material/Chat';
import { Map, Marker, Popup } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { elUpperCase } from '../utils/text';

const ROUTE_TYPE_COLORS = { 0: '#2563eb', 1: '#16a34a', 2: '#9333ea' };

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

const MapPage = () => {
    const { t } = useTranslation();
    const { authTokens, logoutUser, backendUrl, profile, pageTitle } = useContext(AuthContext);
    const [showChildren, setShowChildren] = useState(false);
    const [children, setChildren] = useState([]);
    const allPois = children.flatMap(child => child.pois.map(poi => ({ child, poi })));
    const [loadingChildren, setLoadingChildren] = useState(false);
    const [activeRouteStops, setActiveRouteStops] = useState([]);
    const [popupInfo, setPopupInfo] = useState(null); // { child, poi } | { stop, index } | { type:'routeStop', stop, route }

    const [routes, setRoutes] = useState([]);
    const [selectedRouteId, setSelectedRouteId] = useState('');
    // Active route filter — '' means show all active routes
    const [selectedActiveRouteId, setSelectedActiveRouteId] = useState('');
    const [activeDrivers, setActiveDrivers] = useState([]);
    const [allDrivers, setAllDrivers] = useState([]);
    const [showAllBuses, setShowAllBuses] = useState(false);
    const [todayAbsentChildIds, setTodayAbsentChildIds] = useState(new Set());
    const [pickupToast, setPickupToast] = useState(null);
    const [hoveredRouteId, setHoveredRouteId] = useState(null);
    const [routeEventToast, setRouteEventToast] = useState(null);
    const [errorToast, setErrorToast] = useState(null);
    // Per-stop pickup/skip status for the selected route (polled independently of driver activity)
    const [selectedRouteStopStatus, setSelectedRouteStopStatus] = useState(null); // { [child_id]: { is_picked_up, is_skipped } }
    const seenPickupIds = React.useRef(new Set());
    const seenRouteEventIds = React.useRef(new Set());
    const [showWidget, setShowWidget] = useState(true);
    const [widgetMinimized, setWidgetMinimized] = useState(false);
    const [closingRouteId, setClosingRouteId] = useState(null);
    const [autoZoom, setAutoZoom] = useState(true);
    const [messageDialog, setMessageDialog] = useState(null); // driver object or null
    const [messageText, setMessageText] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    const selectedRoute = routes.find(r => r.id === selectedRouteId) || null;

    // List of currently active routes for the live filter dropdown
    const activeRoutesList = React.useMemo(() =>
        activeRouteStops.map(g => ({ id: g.route_id, name: g.route_name, route_type: g.route_type })),
    [activeRouteStops]);

    // Set of active route IDs for quick lookup
    const activeRouteIds = React.useMemo(() => new Set(activeRoutesList.map(r => r.id)), [activeRoutesList]);

    // Whether the currently selected static route is also an active live route
    const selectedRouteIsActive = React.useMemo(() =>
        !!selectedRouteId && activeRouteIds.has(selectedRouteId),
    [selectedRouteId, activeRouteIds]);

    // Clear selectedActiveRouteId if the route is no longer active
    useEffect(() => {
        if (selectedActiveRouteId && !activeRoutesList.some(r => r.id === selectedActiveRouteId)) {
            setSelectedActiveRouteId('');
        }
    }, [activeRoutesList, selectedActiveRouteId]);

    // School-wide live dashboard stats — optionally filtered to selectedActiveRouteId
    const dashboardStats = React.useMemo(() => {
        let pickedUp = 0, skipped = 0, pending = 0;
        const groups = selectedActiveRouteId
            ? activeRouteStops.filter(g => g.route_id === selectedActiveRouteId)
            : activeRouteStops;
        for (const group of groups) {
            for (const stop of group.stops) {
                if (stop.is_absent) continue;
                if (stop.is_picked_up) pickedUp++;
                else if (stop.is_skipped) skipped++;
                else pending++;
            }
        }
        const routes = selectedActiveRouteId
            ? activeDrivers.filter(d => d.route_id === selectedActiveRouteId).length
            : activeDrivers.length;
        return { routes, pickedUp, skipped, pending };
    }, [activeRouteStops, activeDrivers, selectedActiveRouteId]);

    // child_id → live stop status (from any active route), used to color POI markers
    const activeStopStatusByChildId = React.useMemo(() => {
        const map = {};
        for (const group of activeRouteStops) {
            for (const stop of group.stops) {
                map[stop.child_id] = stop;
            }
        }
        return map;
    }, [activeRouteStops]);

    const mapRef = useRef(null);

    const fitToCoords = useCallback((coords) => {
        if (!mapRef.current || coords.length === 0) return;
        if (coords.length === 1) {
            mapRef.current.flyTo({ center: coords[0], zoom: 15, duration: 800 });
            return;
        }
        const lngs = coords.map(c => c[0]);
        const lats = coords.map(c => c[1]);
        mapRef.current.fitBounds(
            [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
            { padding: 80, maxZoom: 16, duration: 800 }
        );
    }, []);

    const schoolLng = profile?.schoollongitude ? parseFloat(profile.schoollongitude) : null;
    const schoolLat = profile?.schoollatitude ? parseFloat(profile.schoollatitude) : null;
    const hasSchool = schoolLng !== null && schoolLat !== null;

    const fetchChildren = useCallback(async () => {
        setLoadingChildren(true);
        try {
            const res = await fetch(backendUrl + 'api/bus-children/', {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
            });
            if (res.status === 401) { logoutUser(); return; }
            const data = await res.json();
            setChildren(data);
        } catch {
            setErrorToast(t('common.network_error'));
        } finally {
            setLoadingChildren(false);
        }
    }, [backendUrl, authTokens, logoutUser]);

    const fetchRoutes = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/get-routes/', {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
            });
            if (res.status === 401) { logoutUser(); return; }
            setRoutes(await res.json());
        } catch { setErrorToast(t('common.network_error')); }
    }, [backendUrl, authTokens, logoutUser]);

    useEffect(() => { fetchRoutes(); }, []);

    const fetchAllDriverLocations = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/all-driver-locations/', {
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + String(authTokens.access) },
            });
            if (res.ok) setAllDrivers(await res.json());
        } catch (e) { console.error(e); }
    }, [backendUrl, authTokens]);

    const fetchTodayAbsences = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/absences-for-date/', {
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + String(authTokens.access) },
            });
            if (res.ok) {
                const data = await res.json();
                setTodayAbsentChildIds(new Set(data));
            }
        } catch (e) { console.error(e); }
    }, [backendUrl, authTokens]);

    useEffect(() => { fetchTodayAbsences(); }, [fetchTodayAbsences]);

    // Fit map when a static route is selected
    useEffect(() => {
        if (!selectedRoute) return;
        const coords = selectedRoute.stops
            .filter(s => s.latitude && s.longitude)
            .map(s => [parseFloat(s.longitude), parseFloat(s.latitude)]);
        if (hasSchool) coords.push([schoolLng, schoolLat]);
        fitToCoords(coords);
    }, [selectedRouteId]); // eslint-disable-line react-hooks/exhaustive-deps

    const fitToDefault = useCallback(() => {
        const busCoords = activeDrivers
            .filter(d => d.latitude && d.longitude)
            .map(d => [d.longitude, d.latitude]);
        if (busCoords.length > 0) {
            const coords = [...busCoords];
            if (hasSchool) coords.push([schoolLng, schoolLat]);
            fitToCoords(coords);
        } else if (hasSchool && mapRef.current) {
            mapRef.current.flyTo({ center: [schoolLng, schoolLat], zoom: 14, duration: 800 });
        }
    }, [activeDrivers, hasSchool, schoolLng, schoolLat, fitToCoords]);

    // Fit map when children POIs are shown/hidden
    useEffect(() => {
        if (showChildren) {
            if (allPois.length === 0) return;
            const coords = allPois.map(({ poi }) => [parseFloat(poi.longitude), parseFloat(poi.latitude)]);
            if (hasSchool) coords.push([schoolLng, schoolLat]);
            fitToCoords(coords);
        } else {
            fitToDefault();
        }
    }, [showChildren, allPois.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-fit map to school + active bus(es) + all route stops on every position/status update.
    // Skips when: auto-zoom is off (user manually moved map), children POI view is active,
    // or a non-active static route is selected.
    // When an active route is selected (live or static), follows only that route continuously.
    useEffect(() => {
        if (!autoZoom) return; // eslint-disable-line react-hooks/exhaustive-deps
        if (showChildren) return; // eslint-disable-line react-hooks/exhaustive-deps
        if (selectedRouteId && !selectedRouteIsActive) return; // eslint-disable-line react-hooks/exhaustive-deps
        const effectiveRouteId = selectedActiveRouteId || (selectedRouteIsActive ? selectedRouteId : '');
        const drivers = effectiveRouteId
            ? activeDrivers.filter(d => d.route_id === effectiveRouteId)
            : activeDrivers;
        if (drivers.length === 0) return;
        const stopGroups = effectiveRouteId
            ? activeRouteStops.filter(g => g.route_id === effectiveRouteId)
            : activeRouteStops;
        const coords = [];
        if (hasSchool) coords.push([schoolLng, schoolLat]);
        for (const d of drivers) {
            if (d.latitude && d.longitude) coords.push([d.longitude, d.latitude]);
        }
        for (const g of stopGroups) {
            for (const s of g.stops) {
                if (s.latitude && s.longitude) coords.push([s.longitude, s.latitude]);
            }
        }
        fitToCoords(coords);
    }, [activeDrivers, activeRouteStops, selectedActiveRouteId, selectedRouteIsActive]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fit map when "show all buses" is toggled on/off
    useEffect(() => {
        if (showAllBuses) {
            const allBusCoords = [
                ...activeDrivers.filter(d => d.latitude && d.longitude).map(d => [d.longitude, d.latitude]),
                ...allDrivers.filter(d => d.latitude && d.longitude).map(d => [d.longitude, d.latitude]),
            ];
            if (allBusCoords.length === 0) return;
            fitToCoords(allBusCoords);
        } else {
            fitToDefault();
        }
    }, [showAllBuses, allDrivers.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (showAllBuses) fetchAllDriverLocations();
    }, [showAllBuses, fetchAllDriverLocations]);

    const handleToggleAllBuses = () => {
        setShowAllBuses(v => !v);
    };

    const fetchActiveDrivers = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/active-drivers/', {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
            });
            if (res.ok) {
                const data = await res.json();
                setActiveDrivers(data);
            }
        } catch (e) { console.error(e); }
    }, [backendUrl, authTokens]);

    useEffect(() => {
        fetchActiveDrivers();
        const interval = setInterval(fetchActiveDrivers, 3000);
        return () => clearInterval(interval);
    }, [fetchActiveDrivers]);

    const fetchActiveRouteStops = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/active-route-stops/', {
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + String(authTokens.access) },
            });
            if (res.ok) setActiveRouteStops(await res.json());
        } catch (e) { console.error(e); }
    }, [backendUrl, authTokens]);

    const fetchRecentPickups = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/recent-pickups/', {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
            });
            if (res.ok) {
                const data = await res.json();
                for (const p of data) {
                    if (!seenPickupIds.current.has(p.pickup_id)) {
                        seenPickupIds.current.add(p.pickup_id);
                        setPickupToast(p);
                        // Immediately refresh route stop colors on new pickup
                        fetchActiveRouteStops();
                    }
                }
            }
        } catch (e) { console.error(e); }
    }, [backendUrl, authTokens, fetchActiveRouteStops]);

    useEffect(() => {
        fetchRecentPickups();
        const interval = setInterval(fetchRecentPickups, 5000);
        return () => clearInterval(interval);
    }, [fetchRecentPickups]);

    const fetchRecentRouteEvents = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/recent-route-events/', {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
            });
            if (res.ok) {
                const data = await res.json();
                for (const e of data) {
                    if (!seenRouteEventIds.current.has(e.event_id)) {
                        seenRouteEventIds.current.add(e.event_id);
                        setRouteEventToast(e);
                    }
                }
            }
        } catch (e) { console.error(e); }
    }, [backendUrl, authTokens]);

    useEffect(() => {
        fetchRecentRouteEvents();
        const interval = setInterval(fetchRecentRouteEvents, 5000);
        return () => clearInterval(interval);
    }, [fetchRecentRouteEvents]);

    // Always poll activeRouteStops — drives the dashboard strip and the live children overlay
    useEffect(() => {
        fetchActiveRouteStops();
        const interval = setInterval(fetchActiveRouteStops, 5000);
        return () => clearInterval(interval);
    }, [fetchActiveRouteStops]);

    // Poll pickup/skip status for the selected static route independently of driver activity
    const fetchSelectedRouteStopStatus = useCallback(async () => {
        if (!selectedRouteId) return;
        try {
            const res = await fetch(backendUrl + 'api/route-stop-status/?route_id=' + selectedRouteId, {
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + String(authTokens.access) },
            });
            if (res.ok) {
                const data = await res.json();
                const map = {};
                for (const s of data.stops) {
                    map[s.child_id] = { is_picked_up: s.is_picked_up, is_skipped: s.is_skipped };
                }
                setSelectedRouteStopStatus(map);
            }
        } catch (e) { console.error(e); }
    }, [backendUrl, authTokens, selectedRouteId]);

    useEffect(() => {
        if (!selectedRouteId) { setSelectedRouteStopStatus(null); return; }
        fetchSelectedRouteStopStatus();
        const interval = setInterval(fetchSelectedRouteStopStatus, 5000);
        return () => clearInterval(interval);
    }, [selectedRouteId, fetchSelectedRouteStopStatus]);

    const handleToggleChildren = () => {
        if (!showChildren && children.length === 0) {
            fetchChildren();
        }
        setShowChildren(v => !v);
        setPopupInfo(null);
    };

    useEffect(() => {
        document.title = (pageTitle || 'SB2') + ' - ' + t('nav.map');
    }, [pageTitle, t]);

    const handleCloseRoute = useCallback(async (driverProfileId) => {
        if (!window.confirm(t('map.close_route_confirm'))) return;
        setClosingRouteId(driverProfileId);
        try {
            const res = await fetch(backendUrl + 'api/admin/close-route/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + String(authTokens.access) },
                body: JSON.stringify({ driver_profile_id: driverProfileId }),
            });
            if (res.ok) {
                setRouteEventToast({ event_type: 'stopped', driver_name: '', route_name: t('map.route_closed') });
                fetchActiveDrivers();
                fetchActiveRouteStops();
            } else {
                setErrorToast(t('map.close_route_failed'));
            }
        } catch {
            setErrorToast(t('map.close_route_failed'));
        } finally {
            setClosingRouteId(null);
        }
    }, [backendUrl, authTokens, t, fetchActiveDrivers, fetchActiveRouteStops]);

    const handleSendDriverMessage = useCallback(async () => {
        if (!messageDialog || !messageText.trim()) return;
        setSendingMessage(true);
        try {
            const res = await fetch(backendUrl + 'api/admin/send-driver-message/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + String(authTokens.access) },
                body: JSON.stringify({ driver_profile_id: messageDialog.profile_id, message: messageText.trim() }),
            });
            if (res.ok) {
                setMessageDialog(null);
                setMessageText('');
                setErrorToast(null);
                setRouteEventToast({ event_type: 'message_sent', driver_name: '', route_name: t('map.message_driver_sent') });
            } else {
                setErrorToast(t('map.message_driver_failed'));
            }
        } catch {
            setErrorToast(t('map.message_driver_failed'));
        } finally {
            setSendingMessage(false);
        }
    }, [messageDialog, messageText, backendUrl, authTokens, t]);

    if (!profile) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 64px - var(--sat) - var(--sab))' }}>
            {/* Toolbar */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1,
                background: '#fff', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap',
            }}>
                <Button
                    variant={showAllBuses ? 'contained' : 'outlined'}
                    startIcon={<DirectionsBusIcon />}
                    onClick={handleToggleAllBuses}
                    size="small"
                    sx={{
                        fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600,
                        borderRadius: '8px',
                        ...(showAllBuses
                            ? { backgroundColor: '#f59e0b', color: '#0f2d5e', '&:hover': { backgroundColor: '#d97706' } }
                            : { borderColor: '#f59e0b', color: '#d97706', '&:hover': { borderColor: '#d97706', backgroundColor: '#fef3c70a' } }
                        ),
                    }}
                >
                    {showAllBuses ? t('map.hide_all_buses') : t('map.show_all_buses')}
                </Button>

                <Button
                    variant={showChildren ? 'contained' : 'outlined'}
                    startIcon={<PeopleAltIcon />}
                    onClick={handleToggleChildren}
                    disabled={loadingChildren}
                    size="small"
                    sx={{
                        fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600,
                        borderRadius: '8px',
                        ...(showChildren
                            ? { backgroundColor: '#0f2d5e', '&:hover': { backgroundColor: '#163571' } }
                            : { borderColor: '#0f2d5e', color: '#0f2d5e', '&:hover': { borderColor: '#163571', backgroundColor: '#0f2d5e0a' } }
                        ),
                    }}
                >
                    {loadingChildren ? t('map.loading') : (showChildren ? t('map.hide_children') : t('map.show_children'))}
                </Button>

                {showChildren && allPois.length > 0 && (
                    <Chip
                        label={t('map.children_stops', { children: children.length, stops: allPois.length })}
                        size="small"
                        sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', bgcolor: '#f1f5f9', color: '#64748b' }}
                    />
                )}

                {/* Right side: active-route filter + static route selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
                    {/* Reopen active routes widget button — only when widget is hidden and routes are live */}
                    {!showWidget && activeRoutesList.length > 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DirectionsBusIcon />}
                            onClick={() => setShowWidget(true)}
                            sx={{
                                fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600,
                                borderRadius: '8px', borderColor: '#16a34a', color: '#16a34a',
                                '&:hover': { borderColor: '#15803d', backgroundColor: '#16a34a0a' },
                            }}
                        >
                            {t('map.show_active_routes_panel')}
                        </Button>
                    )}
                    {/* Active routes dropdown — only visible when routes are live */}
                    {activeRoutesList.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0,
                                boxShadow: '0 0 0 3px #16a34a30',
                            }} />
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }}>
                                    {t('map.active_route_filter')}
                                </InputLabel>
                                <Select
                                    value={selectedActiveRouteId}
                                    onChange={(e) => { setSelectedActiveRouteId(e.target.value); setPopupInfo(null); }}
                                    label={t('map.active_route_filter')}
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }}
                                >
                                    <MenuItem value="">
                                        <em style={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>
                                            {t('map.all_active_routes')}
                                        </em>
                                    </MenuItem>
                                    {activeRoutesList.map(r => (
                                        <MenuItem key={r.id} value={r.id} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0 }} />
                                                {r.name}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Static route selector */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RouteIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }}>{t('map.show_route')}</InputLabel>
                            <Select
                                value={selectedRouteId}
                                onChange={(e) => {
                                    const routeId = e.target.value;
                                    setSelectedRouteId(routeId);
                                    setPopupInfo(null);
                                    // Sync live filter when an active route is selected
                                    if (routeId && activeRouteIds.has(routeId)) {
                                        setSelectedActiveRouteId(routeId);
                                    } else if (selectedRouteIsActive) {
                                        // Leaving an active route — clear the live filter
                                        setSelectedActiveRouteId('');
                                    }
                                }}
                                label={t('map.show_route')}
                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }}
                            >
                                <MenuItem value=""><em style={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#94a3b8' }}>{t('map.none')}</em></MenuItem>
                                {routes.map(r => {
                                    const isLive = activeRouteIds.has(r.id);
                                    return (
                                    <MenuItem key={r.id} value={r.id} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isLive ? '#16a34a' : ROUTE_TYPE_COLORS[r.route_type], flexShrink: 0,
                                                ...(isLive ? { boxShadow: '0 0 0 2px #16a34a30' } : {}),
                                            }} />
                                            {r.name}
                                            {isLive && (
                                                <Typography component="span" sx={{ fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: '#16a34a', backgroundColor: '#dcfce7', px: 0.6, borderRadius: 0.5 }}>
                                                    LIVE
                                                </Typography>
                                            )}
                                            <Typography component="span" sx={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: '"Outfit", sans-serif' }}>
                                                ({r.stops.length})
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        {selectedRoute && (
                            <Chip
                                label={`${t(`routes.${['to_school','from_school','one_time'][selectedRoute.route_type]}`)} · ${selectedRoute.stops.length} ${t('routes.stops')}`}
                                size="small"
                                sx={{
                                    fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem',
                                    backgroundColor: ROUTE_TYPE_COLORS[selectedRoute.route_type] + '18',
                                    color: ROUTE_TYPE_COLORS[selectedRoute.route_type],
                                    fontWeight: 600,
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Live dashboard strip */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0, px: 0,
                background: '#0f2d5e', borderBottom: '1px solid #1e3a6e',
                overflow: 'hidden',
            }}>
                {dashboardStats.routes === 0 ? (
                    <Typography sx={{
                        fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem',
                        color: 'rgba(255,255,255,0.45)', px: 2, py: 0.75, fontStyle: 'italic',
                    }}>
                        {t('map.dashboard_no_active')}
                    </Typography>
                ) : (
                    <>
                        {[
                            { label: t('map.dashboard_active_routes'), value: dashboardStats.routes, color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
                            { label: t('map.dashboard_picked_up'), value: dashboardStats.pickedUp, color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
                            { label: t('map.dashboard_skipped'), value: dashboardStats.skipped, color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
                            { label: t('map.dashboard_pending'), value: dashboardStats.pending, color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
                        ].map(({ label, value, color, bg }, i) => (
                            <Box key={i} sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                px: 2.5, py: 0.75, flex: 1,
                                background: bg,
                                borderRight: i < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                            }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.1rem', color, lineHeight: 1.1 }}>
                                    {value}
                                </Typography>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)', mt: 0.2, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center' }}>
                                    {elUpperCase(label)}
                                </Typography>
                            </Box>
                        ))}
                    </>
                )}
            </Box>

            {/* Map */}
            <Box sx={{ flex: 1, position: 'relative' }}>
                <Map
                    ref={mapRef}
                    key={`${schoolLng},${schoolLat}`}
                    initialViewState={{
                        longitude: hasSchool ? schoolLng : 0,
                        latitude: hasSchool ? schoolLat : 20,
                        zoom: hasSchool ? 14 : 2,
                    }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle={MAP_STYLE}
                    onClick={() => setPopupInfo(null)}
                    onMoveStart={(e) => { if (e.originalEvent) setAutoZoom(false); }}
                >
                    {/* School marker — always visible */}
                    {hasSchool && (
                        <Marker longitude={schoolLng} latitude={schoolLat} anchor="bottom">
                            <Tooltip title={profile.schoolname || 'School'} arrow>
                                <div style={{ fontSize: '2rem', cursor: 'default', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
                                    🏫
                                </div>
                            </Tooltip>
                        </Marker>
                    )}

                    {/* Children POI markers — colored by live pickup status when a route is active */}
                    {showChildren && allPois.map(({ child, poi }) => {
                        const activeStop = activeStopStatusByChildId[child.child_id];
                        const isAbsent = todayAbsentChildIds.has(child.child_id);
                        const isPickedUp = activeStop?.is_picked_up;
                        const isSkipped = activeStop?.is_skipped;
                        const isPending = activeStop && !isPickedUp && !isSkipped && !isAbsent;
                        const markerBg = isAbsent ? '#9ca3af'
                            : isPickedUp ? '#16a34a'
                            : isSkipped ? '#f59e0b'
                            : isPending ? '#f97316'
                            : '#2563eb';
                        const statusLabel = isAbsent ? t('map.absent_today')
                            : isPickedUp ? (activeStop.pickup_time
                                ? t('map.picked_up_at', { time: new Date(activeStop.pickup_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })
                                : t('map.status_picked_up'))
                            : isSkipped ? t('map.status_skipped')
                            : isPending ? t('map.pending_pickup')
                            : null;
                        return (
                            <Marker
                                key={poi.poi_id}
                                longitude={parseFloat(poi.longitude)}
                                latitude={parseFloat(poi.latitude)}
                                anchor="center"
                                onClick={(e) => { e.originalEvent.stopPropagation(); setPopupInfo({ child, poi }); }}
                            >
                                <Tooltip
                                    title={
                                        <Box sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem' }}>
                                            <Box sx={{ fontWeight: 700 }}>{child.firstname} {child.lastname}</Box>
                                            {statusLabel && <Box sx={{ mt: 0.25, opacity: 0.9 }}>{statusLabel}</Box>}
                                        </Box>
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <div
                                        style={{
                                            width: 28, height: 28, borderRadius: '50%',
                                            backgroundColor: markerBg,
                                            border: isAbsent ? '2px dashed #6b7280' : '2px solid #fff',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#fff', fontWeight: 700, fontSize: 11,
                                            fontFamily: 'Outfit, sans-serif',
                                            cursor: 'pointer',
                                            opacity: isAbsent ? 0.65 : isPickedUp ? 0.85 : 1,
                                        }}
                                    >
                                        {isAbsent ? '✕' : child.firstname.charAt(0)}
                                    </div>
                                </Tooltip>
                            </Marker>
                        );
                    })}

                    {/* Route stop markers (static route view) */}
                    {selectedRoute && (() => {
                        const nextPendingIdx = selectedRoute.stops.findIndex((stop) => {
                            const isAbsent = todayAbsentChildIds.has(stop.child_id);
                            const stopStatus = selectedRouteStopStatus ? selectedRouteStopStatus[stop.child_id] : null;
                            return stopStatus != null && !isAbsent && !stopStatus.is_picked_up && !stopStatus.is_skipped;
                        });
                        return selectedRoute.stops.map((stop, idx) => {
                            const isAbsent = todayAbsentChildIds.has(stop.child_id);
                            const stopStatus = selectedRouteStopStatus ? selectedRouteStopStatus[stop.child_id] : null;
                            const isPickedUp = stopStatus ? stopStatus.is_picked_up : false;
                            const isSkipped = stopStatus ? stopStatus.is_skipped : false;
                            const isPending = stopStatus != null && !isAbsent && !isPickedUp && !isSkipped;
                            const isNext = isPending && idx === nextPendingIdx;
                            const markerBg = isAbsent
                                ? '#9ca3af'
                                : isPickedUp
                                    ? '#16a34a'
                                    : isSkipped
                                        ? '#f59e0b'
                                        : isNext
                                            ? '#f97316'
                                            : isPending
                                                ? '#fb923c'
                                                : ROUTE_TYPE_COLORS[selectedRoute.route_type];
                            const markerBorder = isAbsent ? '2.5px dashed #6b7280' : isNext ? '3px solid #fff' : '2.5px solid #fff';
                            const stopStatusLabel = isAbsent ? t('map.absent_today')
                                : isPickedUp ? t('map.status_picked_up')
                                : isSkipped ? t('map.status_skipped')
                                : isNext ? t('map.next_stop')
                                : isPending ? t('map.pending_pickup')
                                : null;
                            return (
                                <Marker
                                    key={stop.routepoint_id}
                                    longitude={parseFloat(stop.longitude)}
                                    latitude={parseFloat(stop.latitude)}
                                    anchor="center"
                                    onClick={(e) => { e.originalEvent.stopPropagation(); setPopupInfo({ type: 'stop', stop, index: idx, isAbsent, isPickedUp, isSkipped, isPending, isNext }); }}
                                >
                                    <Tooltip
                                        title={
                                            <Box sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem' }}>
                                                <Box sx={{ fontWeight: 700 }}>{stop.child_firstname} {stop.child_lastname}</Box>
                                                {stopStatusLabel && <Box sx={{ mt: 0.25, opacity: 0.9 }}>{stopStatusLabel}</Box>}
                                            </Box>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <div style={{
                                            width: isNext ? 36 : 32, height: isNext ? 36 : 32, borderRadius: '50%',
                                            backgroundColor: markerBg,
                                            border: markerBorder,
                                            boxShadow: isNext ? '0 0 0 3px rgba(249,115,22,0.4), 0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.35)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#fff', fontWeight: 700, fontSize: isAbsent ? 15 : 13,
                                            fontFamily: 'Outfit, sans-serif',
                                            cursor: 'pointer',
                                            opacity: isAbsent ? 0.65 : isPickedUp ? 0.85 : 1,
                                            animation: isNext ? 'pulse 2s infinite' : 'none',
                                        }}>
                                            {isAbsent ? '✕' : idx + 1}
                                        </div>
                                    </Tooltip>
                                </Marker>
                            );
                        });
                    })()}

                    {/* Active route children markers — always visible when routes are live.
                        Color: green=picked up, amber=skipped, orange=pending, gray=absent.
                        Filtered to selectedActiveRouteId when one is chosen. */}
                    {activeRouteStops
                        .filter(g => !selectedActiveRouteId || g.route_id === selectedActiveRouteId)
                        .flatMap(routeGroup => {
                            const nextIdx = routeGroup.stops
                                .filter(s => !s.is_picked_up && !s.is_skipped && !s.is_absent && s.latitude && s.longitude)
                                .reduce((min, s) => s.stop_index < min ? s.stop_index : min, Infinity);
                            return routeGroup.stops
                                .filter(stop => stop.latitude && stop.longitude)
                                .map(stop => {
                                    const isPending = !stop.is_picked_up && !stop.is_skipped && !stop.is_absent;
                                    const isNext = isPending && stop.stop_index === nextIdx;
                                    const bg = stop.is_absent ? '#9ca3af'
                                        : stop.is_picked_up ? '#16a34a'
                                        : stop.is_skipped ? '#f59e0b'
                                        : isNext ? '#f97316'
                                        : '#fb923c';
                                    return (
                                        <Marker
                                            key={`rs-${routeGroup.route_id}-${stop.child_id}`}
                                            longitude={stop.longitude}
                                            latitude={stop.latitude}
                                            anchor="center"
                                            onClick={(e) => { e.originalEvent.stopPropagation(); setPopupInfo({ type: 'routeStop', stop, routeGroup, isNext }); }}
                                        >
                                            <Tooltip
                                                title={
                                                    <Box sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem' }}>
                                                        <Box sx={{ fontWeight: 700 }}>{stop.child_firstname} {stop.child_lastname}</Box>
                                                        <Box sx={{ mt: 0.25, opacity: 0.9 }}>
                                                            {stop.is_absent ? t('map.absent_today')
                                                                : stop.is_picked_up ? (stop.pickup_time
                                                                    ? t('map.picked_up_at', { time: new Date(stop.pickup_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })
                                                                    : t('map.status_picked_up'))
                                                                : stop.is_skipped ? t('map.status_skipped')
                                                                : isNext ? t('map.next_stop')
                                                                : t('map.pending_pickup')}
                                                        </Box>
                                                    </Box>
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                <div style={{
                                                    width: isNext ? 34 : 30,
                                                    height: isNext ? 34 : 30,
                                                    borderRadius: '50%',
                                                    backgroundColor: bg,
                                                    border: stop.is_absent ? '2px dashed #6b7280' : isNext ? '3px solid #fff' : '2.5px solid #fff',
                                                    boxShadow: isNext ? '0 0 0 3px rgba(249,115,22,0.4), 0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.35)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: '#fff', fontWeight: 700, fontSize: 11,
                                                    fontFamily: 'Outfit, sans-serif',
                                                    cursor: 'pointer',
                                                    opacity: stop.is_picked_up ? 0.85 : 1,
                                                    animation: isNext ? 'pulse 2s infinite' : 'none',
                                                }}>
                                                    {stop.is_absent ? '✕' : stop.child_firstname.charAt(0)}
                                                </div>
                                            </Tooltip>
                                        </Marker>
                                    );
                                });
                        })
                    }

                    {/* Popup for clicked child marker */}
                    {popupInfo && !popupInfo.type && (
                        <Popup
                            longitude={parseFloat(popupInfo.poi.longitude)}
                            latitude={parseFloat(popupInfo.poi.latitude)}
                            anchor="top"
                            onClose={() => setPopupInfo(null)}
                            closeButton
                            closeOnClick={false}
                        >
                            <Paper elevation={0} sx={{ p: 0.5, minWidth: 160 }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#0f2d5e' }}>
                                    {popupInfo.child.firstname} {popupInfo.child.lastname}
                                </Typography>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b', mt: 0.25 }}>
                                    {popupInfo.child.schoolclass_name}{popupInfo.child.department_name ? ` · ${popupInfo.child.department_name}` : ''}
                                </Typography>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#475569', mt: 0.5 }}>
                                    {popupInfo.poi.address || popupInfo.poi.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
                                    {popupInfo.child.bus_to_school && (
                                        <Chip label={t('map.to_school_chip')} size="small" sx={{ height: 18, fontSize: '0.62rem', backgroundColor: '#2563eb18', color: '#2563eb' }} />
                                    )}
                                    {popupInfo.child.bus_from_school && (
                                        <Chip label={t('map.from_school_chip')} size="small" sx={{ height: 18, fontSize: '0.62rem', backgroundColor: '#16a34a18', color: '#16a34a' }} />
                                    )}
                                </Box>
                            </Paper>
                        </Popup>
                    )}

                    {/* All buses (last known positions) — shown when showAllBuses is active */}
                    {showAllBuses && allDrivers.filter(d => !activeDrivers.some(a => a.profile_id === d.profile_id)).map((driver) => (
                        driver.latitude && driver.longitude && (
                            <Marker
                                key={'all-' + driver.profile_id}
                                longitude={driver.longitude}
                                latitude={driver.latitude}
                                anchor="center"
                                onClick={(e) => { e.originalEvent.stopPropagation(); setPopupInfo({ type: 'driver', driver: { ...driver, isLastKnown: true } }); }}
                            >
                                <div style={{
                                    width: 36, height: 36, borderRadius: '50%',
                                    backgroundColor: '#9ca3af',
                                    border: '2px dashed #6b7280',
                                    boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    opacity: 0.75,
                                }}>
                                    <span style={{ fontSize: '1rem' }}>🚌</span>
                                </div>
                            </Marker>
                        )
                    ))}

                    {/* Active driver bus markers — filtered to selectedActiveRouteId when set */}
                    {activeDrivers
                        .filter(d => !selectedActiveRouteId || d.route_id === selectedActiveRouteId)
                        .map((driver) => {
                            const isFinished = driver.route_status === 'finished';
                            const isStopped = driver.route_status === 'stopped';
                            const markerColor = isFinished ? '#16a34a' : isStopped ? '#6b7280' : '#f59e0b';
                            return driver.latitude && driver.longitude && (
                                <Marker
                                    key={driver.profile_id}
                                    longitude={driver.longitude}
                                    latitude={driver.latitude}
                                    anchor="center"
                                    onClick={(e) => { e.originalEvent.stopPropagation(); setPopupInfo({ type: 'driver', driver }); }}
                                >
                                    <Tooltip
                                        title={
                                            <Box>
                                                <Typography variant="body2" fontWeight={700}>{driver.firstname} {driver.lastname}</Typography>
                                                {driver.route_name && <Typography variant="caption" display="block">{driver.route_name}</Typography>}
                                                {driver.speed != null && <Typography variant="caption" display="block">{Math.round(driver.speed)} km/h</Typography>}
                                            </Box>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <div style={{
                                            width: 40, height: 40, borderRadius: '50%',
                                            backgroundColor: markerColor,
                                            border: '3px solid #fff',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer',
                                            animation: driver.route_status === 'active' ? 'pulse 2s infinite' : 'none',
                                            position: 'relative',
                                        }}>
                                            <span style={{ fontSize: '1.2rem' }}>🚌</span>
                                            {driver.delay_minutes && (
                                                <div style={{
                                                    position: 'absolute', top: -6, right: -6,
                                                    backgroundColor: driver.delay_minutes < 0 ? '#16a34a' : '#dc2626', borderRadius: '50%',
                                                    width: 18, height: 18,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 9, fontWeight: 700, color: '#fff',
                                                    border: '1.5px solid #fff',
                                                }}>
                                                    {Math.abs(driver.delay_minutes)}
                                                </div>
                                            )}
                                        </div>
                                    </Tooltip>
                                </Marker>
                            );
                        })}

                    {/* Popup for clicked driver marker */}
                    {popupInfo && popupInfo.type === 'driver' && (
                        <Popup
                            longitude={popupInfo.driver.longitude}
                            latitude={popupInfo.driver.latitude}
                            anchor="top"
                            onClose={() => setPopupInfo(null)}
                            closeButton
                            closeOnClick={false}
                        >
                            <Paper elevation={0} sx={{ p: 0.5, minWidth: 180 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <DirectionsBusIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#0f2d5e' }}>
                                        {popupInfo.driver.firstname} {popupInfo.driver.lastname}
                                    </Typography>
                                </Box>
                                {popupInfo.driver.phone_number && (
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
                                        {popupInfo.driver.phone_number}
                                    </Typography>
                                )}
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#475569', mt: 0.5 }}>
                                    {t('map.route_prefix')}: {popupInfo.driver.route_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                    {popupInfo.driver.route_type !== null && (
                                        <Chip
                                            label={t(`routes.${['to_school', 'from_school', 'one_time'][popupInfo.driver.route_type]}`)}
                                            size="small"
                                            sx={{
                                                height: 18, fontSize: '0.62rem',
                                                backgroundColor: (ROUTE_TYPE_COLORS[popupInfo.driver.route_type] || '#666') + '18',
                                                color: ROUTE_TYPE_COLORS[popupInfo.driver.route_type] || '#666',
                                            }}
                                        />
                                    )}
                                    {popupInfo.driver.route_status === 'finished' && (
                                        <Chip label={t('map.status_finished')} size="small" sx={{ height: 18, fontSize: '0.62rem', backgroundColor: '#dcfce7', color: '#16a34a', fontWeight: 700 }} />
                                    )}
                                    {popupInfo.driver.route_status === 'stopped' && (
                                        <Chip label={t('map.status_stopped')} size="small" sx={{ height: 18, fontSize: '0.62rem', backgroundColor: '#f1f5f9', color: '#6b7280', fontWeight: 700 }} />
                                    )}
                                    {popupInfo.driver.delay_minutes && (
                                        <Chip
                                            label={popupInfo.driver.delay_minutes < 0
                                                ? t('map.early_min', { minutes: Math.abs(popupInfo.driver.delay_minutes) })
                                                : t('map.delayed_min', { minutes: popupInfo.driver.delay_minutes })}
                                            size="small"
                                            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                                backgroundColor: popupInfo.driver.delay_minutes < 0 ? '#dcfce7' : '#fee2e2',
                                                color: popupInfo.driver.delay_minutes < 0 ? '#16a34a' : '#dc2626',
                                            }}
                                        />
                                    )}
                                </Box>
                                {popupInfo.driver.isLastKnown && (
                                    <Chip label={t('map.last_known')} size="small" sx={{ height: 17, fontSize: '0.62rem', backgroundColor: '#f1f5f9', color: '#6b7280', fontWeight: 700, mb: 0.5 }} />
                                )}
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.62rem', color: '#94a3b8', mt: 0.5 }}>
                                    {t('map.updated_at')}: {popupInfo.driver.last_update ? new Date(popupInfo.driver.last_update).toLocaleTimeString() : '—'}
                                </Typography>
                            </Paper>
                        </Popup>
                    )}

                    {/* Popup for active route child marker */}
                    {popupInfo && popupInfo.type === 'routeStop' && (
                        <Popup
                            longitude={popupInfo.stop.longitude}
                            latitude={popupInfo.stop.latitude}
                            anchor="top"
                            onClose={() => setPopupInfo(null)}
                            closeButton
                            closeOnClick={false}
                        >
                            <Paper elevation={0} sx={{ p: 0.5, minWidth: 170 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box sx={{
                                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                        backgroundColor: popupInfo.stop.is_absent ? '#9ca3af' : popupInfo.stop.is_picked_up ? '#16a34a' : popupInfo.stop.is_skipped ? '#f59e0b' : '#f97316',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', fontWeight: 700, fontSize: 11,
                                    }}>
                                        {popupInfo.stop.child_firstname.charAt(0)}
                                    </Box>
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#0f2d5e' }}>
                                        {popupInfo.stop.child_firstname} {popupInfo.stop.child_lastname}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
                                    {popupInfo.routeGroup.route_name}
                                </Typography>
                                <Chip
                                    label={
                                        popupInfo.stop.is_absent ? t('map.absent_today')
                                        : popupInfo.stop.is_picked_up ? t('map.picked_up_at', { time: new Date(popupInfo.stop.pickup_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })
                                        : popupInfo.stop.is_skipped ? t('map.status_skipped')
                                        : popupInfo.isNext ? t('map.next_stop')
                                        : t('map.pending_pickup')
                                    }
                                    size="small"
                                    sx={{
                                        mt: 0.75, height: 19, fontSize: '0.65rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600,
                                        backgroundColor: popupInfo.stop.is_absent ? '#f1f5f9' : popupInfo.stop.is_picked_up ? '#dcfce7' : popupInfo.stop.is_skipped ? '#fffbeb' : '#fff7ed',
                                        color: popupInfo.stop.is_absent ? '#6b7280' : popupInfo.stop.is_picked_up ? '#16a34a' : popupInfo.stop.is_skipped ? '#d97706' : '#f97316',
                                        fontWeight: popupInfo.isNext ? 800 : 600,
                                    }}
                                />
                            </Paper>
                        </Popup>
                    )}

                    {/* Popup for clicked static route stop */}
                    {popupInfo && popupInfo.type === 'stop' && (
                        <Popup
                            longitude={parseFloat(popupInfo.stop.longitude)}
                            latitude={parseFloat(popupInfo.stop.latitude)}
                            anchor="top"
                            onClose={() => setPopupInfo(null)}
                            closeButton
                            closeOnClick={false}
                        >
                            <Paper elevation={0} sx={{ p: 0.5, minWidth: 170 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box sx={{
                                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                        backgroundColor: popupInfo.isAbsent ? '#9ca3af' : ROUTE_TYPE_COLORS[selectedRoute.route_type],
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', fontWeight: 700, fontSize: 11,
                                    }}>
                                        {popupInfo.index + 1}
                                    </Box>
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#0f2d5e' }}>
                                        {popupInfo.stop.child_firstname} {popupInfo.stop.child_lastname}
                                    </Typography>
                                </Box>
                                {popupInfo.isAbsent && (
                                    <Chip label={t('map.absent_today')} size="small" sx={{ height: 17, fontSize: '0.62rem', backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: 700, mb: 0.5 }} />
                                )}
                                {!popupInfo.isAbsent && popupInfo.isPickedUp && (
                                    <Chip label={t('map.status_picked_up')} size="small" sx={{ height: 17, fontSize: '0.62rem', backgroundColor: '#dcfce7', color: '#16a34a', fontWeight: 700, mb: 0.5 }} />
                                )}
                                {!popupInfo.isAbsent && popupInfo.isSkipped && (
                                    <Chip label={t('map.status_skipped')} size="small" sx={{ height: 17, fontSize: '0.62rem', backgroundColor: '#fffbeb', color: '#d97706', fontWeight: 700, mb: 0.5 }} />
                                )}
                                {!popupInfo.isAbsent && popupInfo.isPending && !popupInfo.isNext && (
                                    <Chip label={t('map.pending_pickup')} size="small" sx={{ height: 17, fontSize: '0.62rem', backgroundColor: '#fff7ed', color: '#f97316', fontWeight: 700, mb: 0.5 }} />
                                )}
                                {!popupInfo.isAbsent && popupInfo.isNext && (
                                    <Chip label={t('map.next_stop')} size="small" sx={{ height: 17, fontSize: '0.62rem', backgroundColor: '#fff7ed', color: '#f97316', fontWeight: 800, mb: 0.5 }} />
                                )}
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
                                    {popupInfo.stop.schoolclass_name}{popupInfo.stop.department_name ? ` · ${popupInfo.stop.department_name}` : ''}
                                </Typography>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#475569', mt: 0.5 }}>
                                    {popupInfo.stop.address || popupInfo.stop.name}
                                </Typography>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: '#94a3b8', mt: 0.5 }}>
                                    {t('map.stop_of', { index: popupInfo.index + 1, total: selectedRoute.stops.length, route: selectedRoute.name })}
                                </Typography>
                            </Paper>
                        </Popup>
                    )}
                </Map>

                {/* Auto Zoom toggle button — bottom-left overlay */}
                <Box sx={{ position: 'absolute', bottom: 28, left: 12, zIndex: 10 }}>
                    <Button
                        size="small"
                        onClick={() => setAutoZoom(true)}
                        startIcon={<CenterFocusStrongIcon sx={{ fontSize: '1rem !important' }} />}
                        sx={{
                            fontFamily: '"Outfit", sans-serif',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            borderRadius: '8px',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            ...(autoZoom ? {
                                background: 'rgba(22, 163, 74, 0.15)',
                                border: '1px solid rgba(22, 163, 74, 0.4)',
                                color: '#16a34a',
                                '&:hover': { background: 'rgba(22, 163, 74, 0.25)' },
                            } : {
                                background: 'rgba(245, 158, 11, 0.92)',
                                border: '1px solid #f59e0b',
                                color: '#fff',
                                boxShadow: '0 2px 10px rgba(245,158,11,0.5)',
                                animation: 'pulse 2s infinite',
                                '&:hover': { background: 'rgba(217, 119, 6, 0.95)' },
                            }),
                        }}
                    >
                        {t('map.auto_zoom')}
                    </Button>
                </Box>

                {/* Active routes glass widget — rendered after Map so it sits on top */}
                {showWidget && activeRoutesList.length > 0 && (
                    <Box sx={{
                        position: 'absolute', right: 12, top: 12, zIndex: 10,
                        width: 272,
                        background: 'rgba(10, 25, 55, 0.72)',
                        backdropFilter: 'blur(14px)',
                        WebkitBackdropFilter: 'blur(14px)',
                        border: '1px solid rgba(255,255,255,0.13)',
                        borderRadius: '14px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                        overflow: 'hidden',
                    }}>
                        {/* Widget header */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', px: 1.5, py: 1,
                            borderBottom: widgetMinimized ? 'none' : '1px solid rgba(255,255,255,0.1)',
                        }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#16a34a', mr: 1, flexShrink: 0, boxShadow: '0 0 0 2px #16a34a40' }} />
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.8rem', color: '#f1f5f9', flex: 1 }}>
                                {t('map.active_routes_widget')}
                                <Typography component="span" sx={{ ml: 0.75, fontSize: '0.68rem', fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: '#f59e0b' }}>
                                    {activeRoutesList.length}
                                </Typography>
                            </Typography>
                            <Tooltip title={widgetMinimized ? t('map.show_active_routes_panel') : 'Minimize'} arrow>
                                <IconButton size="small" onClick={() => setWidgetMinimized(v => !v)} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.4, '&:hover': { color: '#fff' } }}>
                                    {widgetMinimized ? <AddIcon sx={{ fontSize: 14 }} /> : <RemoveIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Close" arrow>
                                <IconButton size="small" onClick={() => setShowWidget(false)} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.4, ml: 0.25, '&:hover': { color: '#fff' } }}>
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Route rows */}
                        {!widgetMinimized && (
                            <Box sx={{ py: 0.5 }}>
                                {activeDrivers.map(driver => {
                                    const routeGroup = activeRouteStops.find(g => g.route_id === driver.route_id);
                                    const stops = routeGroup ? routeGroup.stops.filter(s => !s.is_absent) : [];
                                    const pickedUp = stops.filter(s => s.is_picked_up).length;
                                    const pending = stops.filter(s => !s.is_picked_up && !s.is_skipped).length;
                                    const isSelected = selectedActiveRouteId === driver.route_id;
                                    const isClosing = closingRouteId === driver.profile_id;
                                    return (
                                        <Box
                                            key={driver.profile_id}
                                            onClick={() => {
                                                setSelectedActiveRouteId(isSelected ? '' : driver.route_id);
                                                setPopupInfo(null);
                                            }}
                                            onMouseEnter={() => setHoveredRouteId(driver.profile_id)}
                                            onMouseLeave={() => setHoveredRouteId(null)}
                                            sx={{
                                                px: 1.5, py: 0.9, cursor: 'pointer',
                                                borderLeft: isSelected ? '3px solid #f59e0b' : '3px solid transparent',
                                                background: isSelected ? 'rgba(245,158,11,0.1)' : 'transparent',
                                                '&:hover': { background: 'rgba(255,255,255,0.06)' },
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.3 }}>
                                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, backgroundColor: ROUTE_TYPE_COLORS[driver.route_type] || '#16a34a' }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.78rem', color: '#f1f5f9', flex: 1, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {driver.route_name}
                                                </Typography>
                                                <Tooltip title={t('map.message_driver')} arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); setMessageDialog(driver); setMessageText(''); }}
                                                        sx={{ p: 0.3, color: 'rgba(148,163,184,0.7)', '&:hover': { color: '#93c5fd', background: 'rgba(147,197,253,0.12)' }, flexShrink: 0 }}
                                                    >
                                                        <ChatIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('map.close_route')} arrow>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isClosing}
                                                        onClick={(e) => { e.stopPropagation(); handleCloseRoute(driver.profile_id); }}
                                                        sx={{ p: 0.3, color: 'rgba(248,113,113,0.7)', '&:hover': { color: '#f87171', background: 'rgba(248,113,113,0.12)' }, flexShrink: 0 }}
                                                    >
                                                        <StopCircleIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: 'rgba(203,213,225,0.7)', mb: 0.25 }}>
                                                {driver.firstname} {driver.lastname}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', color: '#34d399' }}>
                                                    ✓ {pickedUp}
                                                </Typography>
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', color: '#94a3b8' }}>
                                                    ◎ {pending}
                                                </Typography>
                                                {driver.delay_minutes && (
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', color: driver.delay_minutes < 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>
                                                        {driver.delay_minutes > 0 ? '+' : ''}{driver.delay_minutes}m
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Collapse in={hoveredRouteId === driver.profile_id} timeout={150} unmountOnExit>
                                                <Box sx={{ mt: 0.75, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.1)', maxHeight: 220, overflowY: 'auto' }}>
                                                    {[...stops].sort((a, b) => a.stop_index - b.stop_index).map(stop => {
                                                        const isDropOff = driver.route_type === 1;
                                                        const nextStop = stops
                                                            .filter(x => !x.is_picked_up && !x.is_skipped)
                                                            .reduce((min, x) => (min == null || x.stop_index < min.stop_index ? x : min), null);
                                                        const isNext = nextStop && nextStop.child_id === stop.child_id;
                                                        const color = stop.is_picked_up ? '#34d399' : stop.is_skipped ? '#f59e0b' : isNext ? '#f97316' : '#94a3b8';
                                                        const time = stop.is_picked_up && stop.pickup_time
                                                            ? new Date(stop.pickup_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                            : null;
                                                        const statusText = stop.is_picked_up
                                                            ? (time
                                                                ? t(isDropOff ? 'map.dropped_off_at' : 'map.picked_up_at', { time })
                                                                : t(isDropOff ? 'map.status_dropped_off' : 'map.status_picked_up'))
                                                            : stop.is_skipped ? t('map.status_skipped')
                                                            : isNext ? t('map.next_stop')
                                                            : t(isDropOff ? 'map.pending_dropoff' : 'map.pending_pickup');
                                                        return (
                                                            <Box key={stop.child_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, backgroundColor: color }} />
                                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: stop.is_picked_up ? 'rgba(203,213,225,0.6)' : '#f1f5f9', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {stop.child_firstname} {stop.child_lastname}
                                                                </Typography>
                                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.6rem', color, flexShrink: 0 }}>
                                                                    {statusText}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Pickup toast */}
            <Snackbar
                open={!!pickupToast}
                autoHideDuration={20000}
                onClose={() => setPickupToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {pickupToast ? (
                    <Alert
                        onClose={() => setPickupToast(null)}
                        severity="success"
                        variant="filled"
                        sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}
                    >
                        {pickupToast.driver_name} {pickupToast.route_type === 1 ? t('map.dropped_off') : t('map.picked_up')} {pickupToast.child_name} — {pickupToast.route_name}
                    </Alert>
                ) : undefined}
            </Snackbar>

            {/* Route event toast (start / stop / finish) */}
            <Snackbar
                open={!!routeEventToast}
                autoHideDuration={20000}
                onClose={() => setRouteEventToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {routeEventToast ? (
                    <Alert
                        onClose={() => setRouteEventToast(null)}
                        severity={routeEventToast.event_type === 'start' ? 'info' : routeEventToast.event_type === 'finished' ? 'success' : routeEventToast.event_type === 'message_sent' ? 'success' : 'warning'}
                        variant="filled"
                        sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}
                    >
                        {routeEventToast.event_type === 'start'
                            ? t('map.route_started', { driver: routeEventToast.driver_name, route: routeEventToast.route_name })
                            : routeEventToast.event_type === 'finished'
                                ? t('map.route_finished', { driver: routeEventToast.driver_name, route: routeEventToast.route_name })
                                : routeEventToast.event_type === 'message_sent'
                                    ? routeEventToast.route_name
                                    : t('map.route_stopped', { driver: routeEventToast.driver_name, route: routeEventToast.route_name })}
                    </Alert>
                ) : undefined}
            </Snackbar>

            {/* Error toast */}
            <Snackbar
                open={!!errorToast}
                autoHideDuration={20000}
                onClose={() => setErrorToast(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                {errorToast ? (
                    <Alert onClose={() => setErrorToast(null)} severity="error" variant="filled" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}>
                        {errorToast}
                    </Alert>
                ) : undefined}
            </Snackbar>

            <Dialog
                open={!!messageDialog}
                onClose={() => { if (!sendingMessage) { setMessageDialog(null); setMessageText(''); } }}
                PaperProps={{
                    sx: {
                        background: 'rgba(15, 23, 42, 0.97)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        minWidth: 340,
                    }
                }}
            >
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: '#f1f5f9', fontSize: '1rem', pb: 1 }}>
                    {t('map.message_driver_title')}
                    {messageDialog && (
                        <Typography component="span" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 400, fontSize: '0.8rem', color: '#94a3b8', ml: 1 }}>
                            — {messageDialog.firstname} {messageDialog.lastname}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    <TextField
                        autoFocus
                        multiline
                        minRows={3}
                        maxRows={6}
                        fullWidth
                        placeholder={t('map.message_driver_placeholder')}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSendDriverMessage(); }}
                        disabled={sendingMessage}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: '"Outfit", sans-serif',
                                fontSize: '0.875rem',
                                color: '#f1f5f9',
                                background: 'rgba(255,255,255,0.05)',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                            },
                            '& .MuiInputBase-input::placeholder': { color: 'rgba(148,163,184,0.6)', opacity: 1 },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => { setMessageDialog(null); setMessageText(''); }}
                        disabled={sendingMessage}
                        sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', textTransform: 'none' }}
                    >
                        {t('common.cancel') || 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSendDriverMessage}
                        disabled={sendingMessage || !messageText.trim()}
                        startIcon={sendingMessage ? <CircularProgress size={14} color="inherit" /> : <ChatIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            fontFamily: '"Outfit", sans-serif',
                            fontWeight: 600,
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                            '&:hover': { background: 'linear-gradient(135deg, #1e40af, #1d4ed8)' },
                            '&.Mui-disabled': { opacity: 0.5 },
                        }}
                    >
                        {t('map.message_driver_send')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MapPage;
